import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";

const ROLES = ["artist", "label", "venue", "zine", "collective", "ally"];

export default function SubmitPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    role: "artist",
    link: "",
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Name, email and message are required.");
      return;
    }
    setSending(true);
    try {
      await api.post("/submissions", form);
      setDone(true);
      toast.success("Transmission received. We will be in touch.");
      setForm({ name: "", email: "", role: "artist", link: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12 py-16 lg:py-24" data-testid="submit-page">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5 warm-up">
          <span className="label-accent">// Open channel</span>
          <h1 className="broadcast-headline text-5xl lg:text-6xl mt-4 text-[color:var(--nccc-cream)] chromatic">
            Submit a
            <br />
            transmission.
          </h1>
          <p className="mt-8 text-[color:var(--nccc-muted)] leading-relaxed">
            Artists, labels, venues, zines, broadcasters — send us your work.
            We are actively seeking contributors and collaborators across the
            North.
          </p>
          <ul className="mt-8 space-y-3 text-sm text-[color:var(--nccc-cream)]">
            <li><span className="hex-bullet" /> Demos, tracks, releases.</li>
            <li><span className="hex-bullet" /> Events, zines, broadcasts to list under Portals.</li>
            <li><span className="hex-bullet" /> Allied collectives and venues for the mesh.</li>
          </ul>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="submit-transmission-form"
          className="lg:col-span-7 border border-[color:var(--nccc-border)] bg-[color:var(--nccc-surface)]/60 p-6 sm:p-10 warm-up-2"
        >
          {done ? (
            <div className="py-12 text-center" data-testid="submission-success">
              <span className="label-accent">// Transmission received</span>
              <h3 className="font-display text-3xl text-[color:var(--nccc-cream)] mt-4">
                The signal is logged.
              </h3>
              <p className="mt-3 text-[color:var(--nccc-muted)]">
                We respond in waves. Patience — the analogue is slower.
              </p>
              <button
                onClick={() => setDone(false)}
                className="nccc-btn nccc-btn-ghost mt-8"
                data-testid="send-another-btn"
              >
                Send another
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="label">Name</label>
                <input
                  data-testid="submit-input-name"
                  className="nccc-input mt-2"
                  value={form.name}
                  onChange={onChange("name")}
                  required
                />
              </div>
              <div>
                <label className="label">Email</label>
                <input
                  type="email"
                  data-testid="submit-input-email"
                  className="nccc-input mt-2"
                  value={form.email}
                  onChange={onChange("email")}
                  required
                />
              </div>
              <div>
                <label className="label">You are</label>
                <select
                  data-testid="submit-input-role"
                  className="nccc-input mt-2"
                  value={form.role}
                  onChange={onChange("role")}
                >
                  {ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Link (Bandcamp, YouTube, etc.)</label>
                <input
                  data-testid="submit-input-link"
                  className="nccc-input mt-2"
                  value={form.link}
                  onChange={onChange("link")}
                />
              </div>
              <div className="sm:col-span-2">
                <label className="label">Message</label>
                <textarea
                  data-testid="submit-input-message"
                  className="nccc-input mt-2 min-h-[160px] resize-none"
                  value={form.message}
                  onChange={onChange("message")}
                  required
                />
              </div>
              <div className="sm:col-span-2 flex items-center justify-between mt-2">
                <span className="label">// END OF FORM</span>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="submit-transmission-btn"
                  className="nccc-btn"
                >
                  {sending ? "Sending..." : "Send transmission →"}
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
